import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";

import {
  convertToDateString,
  DateOfBirthValidater,
  isUrdu,
  LoadingSpinner,
} from "../../constants/const";
import { ApiResponsibility } from "../config/responsibilityType/ApiResponsibilityType";
import { GetLanguageString } from "../helper/Components";
import { addMember } from "../member/ApiMember";
import useForm from "../shared/useForm";

const initialValues = {
  fullName: "",
  fatherName: "",
  cnic: "",
  phone: "",
  currentAddress: "",
  referenceNote: "",
  responsibilityId: 0,
  responsibilityLabel: "",
  responsibilityDate: null,
  displayName: "",
  joiningDate: null,
};

const formatCnic = (cnic) => {
  if (!cnic) return "";

  const rawCnic = cnic.toString().replace(/-/g, "");
  if (rawCnic.length !== 13) return cnic;

  return `${rawCnic.slice(0, 5)}-${rawCnic.slice(5, 12)}-${rawCnic.slice(12)}`;
};

const toDate = (date) => (date ? new Date(date) : null);

const getValue = (record, fields, defaultValue = "") => {
  for (const field of fields) {
    if (
      record?.[field] !== undefined &&
      record?.[field] !== null &&
      record?.[field] !== ""
    ) {
      return record[field];
    }
  }

  return defaultValue;
};

const EditNonRafiqUserModal = ({ editValue, setModal, onSaved }) => {
  const toggle = () => {
    setModal(false);
  };
  const { t } = useTranslation();
  const [selectedDateOfBirth, setSelectedDateOfBirth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responsibilityOptions, setResponsibilityOptions] = useState([]);
  const { errors, setErrors, values, setValues, handleInputChange } =
    useForm(initialValues);
  const getTextClassName = (value, baseClassName = "") =>
    [baseClassName, isUrdu(value) ? "ur" : ""].filter(Boolean).join(" ");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      ApiResponsibility().GetNonRafiqReponsibilities(),
      addMember().GetNonRafiqUserDetails(editValue),
    ])
      .then(([responsibilityRes, userRes]) => {
        const userDetails = userRes.data;
        const responsibility = getValue(userDetails, [
          "Responsibility",
          "responsibility",
        ], null);
        const responsibilityId = getValue(
          responsibility,
          ["Value", "value"],
          0
        );

        setResponsibilityOptions(responsibilityRes.data);
        setSelectedDateOfBirth(
          toDate(getValue(userDetails, ["BirthDate", "birthDate"], null))
        );
        setValues({
          fullName: getValue(userDetails, ["FullName", "fullName"]),
          fatherName: getValue(userDetails, ["FatherName", "fatherName"]),
          cnic: formatCnic(getValue(userDetails, ["CNIC", "cnic"])),
          phone: getValue(userDetails, ["contactNo", "mobileNumber", "phone"]),
          currentAddress: getValue(userDetails, ["address", "currentAddress"]),
          referenceNote: getValue(userDetails, [
            "ReferenceNote",
            "referenceNote",
          ]),
          responsibilityId,
          responsibilityLabel: getValue(responsibility, ["Label", "label"]),
          responsibilityDate: toDate(
            getValue(
              userDetails,
              ["ResponsibilityDate", "responsibilityDate"],
              null
            )
          ),
          displayName: getValue(userDetails, [
            "ResponsibilityDisplayName",
            "responsibilityDisplayName",
            "DisplayName",
            "displayName",
          ]),
        });
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [editValue, setValues]);

  const validate = () => {
    let temp = {};

    temp.fullName =
      values.fullName !== "" ? (
        ""
      ) : (
        <GetLanguageString props="member_first_name_error_message" />
      );
    temp.fatherName =
      values.fatherName !== "" ? (
        ""
      ) : (
        <GetLanguageString props="member_father_name_error_message" />
      );
    temp.cnic =
      values.cnic !== "" ? (
        values.cnic.replace(/-/g, "").length === 13 ? (
          ""
        ) : (
          "CNIC is not valid. It must be 13 numbers."
        )
      ) : (
        <GetLanguageString props="member_cnic_error_message" />
      );
    temp.birthDate =
      selectedDateOfBirth !== null ? (
        DateOfBirthValidater(selectedDateOfBirth) ? (
          ""
        ) : (
          <GetLanguageString props="member_birth_date_less_error_message" />
        )
      ) : (
        <GetLanguageString props="member_birth_date_error_message" />
      );
    const selectedResponsibility = responsibilityOptions.find(
      (opt) => opt.value === values.responsibilityId
    );
    if (selectedResponsibility?.isMultiple) {
      temp.displayName =
        values.displayName !== "" ? (
          ""
        ) : (
          <GetLanguageString props="responsibility_title_err" />
        );
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleResponsibilityChange = (record) => {
    if (record !== null) {
      setValues({
        ...values,
        responsibilityId: record.value,
        responsibilityLabel: record.label,
        displayName: record.isMultiple ? values.displayName : "",
      });
    } else {
      setValues({
        ...values,
        responsibilityId: 0,
        responsibilityLabel: "",
        displayName: "",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const payload = {
      responsibilityDisplayName: showDisplayName ? values.displayName : "",
      responsibilityDate: values.responsibilityDate
        ? convertToDateString(values.responsibilityDate)
        : null,
      responsibilityId: values.responsibilityId,
      referenceNote: values.referenceNote,
      currentAddress: values.currentAddress,
      mobileNumber: values.phone,
      fatherName: values.fatherName,
      fullName: values.fullName,
      cnic: values.cnic ? values.cnic.replace(/-/g, "") : "",
      birthDate: convertToDateString(selectedDateOfBirth),
    };

    addMember()
      .EditNonRafiqUsers(editValue, payload)
      .then(() => {
        setLoading(false);
        swal({
          title: t("Edited"),
          icon: "success",
          buttons: "OK",
        });
        onSaved();
        toggle();
      })
      .catch((err) => {
        setLoading(false);
        swal({
          title: err.response?.data || err.message || "Error",
          icon: "error",
          buttons: "OK",
        });
        console.log(err);
      });
  };

  const selectedResponsibility = responsibilityOptions.find(
    (opt) => opt.value === values.responsibilityId
  );
  const showDisplayName = selectedResponsibility?.isMultiple;

  return (
    <Modal size="xl" isOpen={true} className="inmodal" autoFocus={false}>
      <ModalHeader className="modal-title" tag="h4">
        {<GetLanguageString props="common_edit" />}
      </ModalHeader>
      <ModalBody className="py-3">
        {loading ? (
          LoadingSpinner()
        ) : (
          <div className="d-flex flex-column">
            <Card className="mb-2 border shadow-none">
              <CardBody className="p-3">
                <h5 className="mb-2 pb-2 border-bottom font-weight-bold">
                  {<GetLanguageString props="user_details" />}
                </h5>
                <Row className="g-2">
                  <Col md="6">
                    <FormGroup className="mb-2">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_first_name" />}
                      </Label>
                      <Input
                        className={getTextClassName(values.fullName)}
                        type="text"
                        name="fullName"
                        value={values.fullName}
                        onChange={handleInputChange}
                      />
                      {errors.fullName && (
                        <h4 className="text-error">{errors.fullName}</h4>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="mb-2">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_father_name" />}
                      </Label>
                      <Input
                        className={getTextClassName(values.fatherName)}
                        type="text"
                        name="fatherName"
                        value={values.fatherName}
                        onChange={handleInputChange}
                      />
                      {errors.fatherName && (
                        <h4 className="text-error">{errors.fatherName}</h4>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col md="6">
                    <FormGroup className="mb-2">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_cnic" />}
                      </Label>
                      <InputMask
                        mask="99999-9999999-9"
                        value={values.cnic}
                        onChange={handleInputChange}
                        name="cnic"
                        maskChar=""
                      >
                        {(inputProps) => (
                          <Input
                            {...inputProps}
                            type="text"
                            className="no-drag"
                            placeholder="______-_______-_"
                          />
                        )}
                      </InputMask>
                      {errors.cnic && (
                        <h4 className="text-error">{errors.cnic}</h4>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="mb-2">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_date_of_birth" />}
                      </Label>
                      <DatePicker
                        showYearDropdown
                        dropdownMode="select"
                        placeholderText="dd/mm/yyyy"
                        className="form-control"
                        selected={selectedDateOfBirth}
                        maxDate={new Date()}
                        onChange={(value) => setSelectedDateOfBirth(value)}
                        dateFormat="dd/MM/yyyy"
                      />
                      {errors.birthDate && (
                        <h4 className="text-error">{errors.birthDate}</h4>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col md="6">
                    <FormGroup className="mb-2">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_phone" />}
                      </Label>
                      <Input
                        className={getTextClassName(values.phone)}
                        type="text"
                        name="phone"
                        value={values.phone}
                        onChange={handleInputChange}
                      />
                      {errors.phone && (
                        <h4 className="text-error">{errors.phone}</h4>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="mb-2">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_address" />}
                      </Label>
                      <Input
                        className={getTextClassName(values.currentAddress)}
                        type="text"
                        name="currentAddress"
                        value={values.currentAddress}
                        onChange={handleInputChange}
                      />
                      {errors.currentAddress && (
                        <h4 className="text-error">{errors.currentAddress}</h4>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col md="12">
                    <FormGroup className="mb-0">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_reference_note" />}
                      </Label>
                      <Input
                        className={getTextClassName(values.referenceNote)}
                        type="text"
                        name="referenceNote"
                        value={values.referenceNote}
                        onChange={handleInputChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            <Card className="mb-0 border shadow-none">
              <CardBody className="p-3">
                <h5 className="mb-2 pb-2 border-bottom font-weight-bold">
                  {<GetLanguageString props="member_responsibility" />}
                </h5>
                <Row className="g-2">
                  <Col md={showDisplayName ? "4" : "6"}>
                    <FormGroup className="mb-0">
                      <Label className="col-form-label">
                        {<GetLanguageString props="member_select_responsibilities" />}
                      </Label>
                      <Select
                        options={responsibilityOptions}
                        value={
                          responsibilityOptions.find(
                            (opt) => opt.value === values.responsibilityId
                          ) || null
                        }
                        className="basic-single ur"
                        classNamePrefix="select"
                        placeholder={"Select"}
                        isClearable={true}
                        onChange={handleResponsibilityChange}
                      />
                    </FormGroup>
                  </Col>
                  {showDisplayName && (
                    <Col md="4">
                      <FormGroup className="mb-0">
                        <Label className="col-form-label">
                          {<GetLanguageString props="responsibility_title" />}
                        </Label>
                        <Input
                          className={getTextClassName(
                            values.displayName,
                            "no-drag"
                          )}
                          type="text"
                          name="displayName"
                          value={values.displayName}
                          onChange={handleInputChange}
                          {...(errors?.displayName && { invalid: true })}
                        />
                        {errors.displayName && (
                          <h4 className="text-error">{errors.displayName}</h4>
                        )}
                      </FormGroup>
                    </Col>
                  )}
                  <Col md={showDisplayName ? "4" : "6"}>
                    <FormGroup className="mb-0">
                      <Label className="col-form-label">
                        {
                          <GetLanguageString props="member_member_detail_common_responsibility_date" />
                        }
                      </Label>
                      <DatePicker
                        showYearDropdown
                        dropdownMode="select"
                        placeholderText="dd/mm/yyyy"
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                        selected={values.responsibilityDate}
                        maxDate={new Date()}
                        onChange={(value) =>
                          setValues({ ...values, responsibilityDate: value })
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="white" onClick={toggle}>
          {<GetLanguageString props="common_cancel" />}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {<GetLanguageString props="common_save_changes" />}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditNonRafiqUserModal;
